import type { Channel, Vod } from '../types'

const KICK_API = 'https://kick.com/api/v2'
/** vyneer.me's mirror of the same endpoint; used only if kick.com itself fails. */
const KICK_API_MIRROR = 'https://kapi.vyneer.me/api/v2'

export class ApiError extends Error {
  readonly status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

/**
 * Accepts a bare slug (`xqc`, `XQC`) or any Kick channel URL
 * (`kick.com/xqc`, `https://kick.com/xqc/videos`) and returns the slug.
 * Returns null when nothing usable can be extracted.
 */
export function parseSlug(input: string): string | null {
  const trimmed = input.trim()
  if (!trimmed) return null

  // Strip scheme first, then host. Doing this in two steps matters: searching
  // for the first "/" before removing `https://` would slice into the protocol.
  const withoutScheme = trimmed.replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
  const withoutHost = withoutScheme.replace(/^[^/]*\.[^/]*(?=\/|$)/i, '')

  const first = withoutHost.split(/[?#]/)[0].split('/').filter(Boolean)[0]
  if (!first) return null

  return first.toLowerCase().replace(/[^a-z0-9_-]/g, '') || null
}

async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  let res: Response
  try {
    res = await fetch(url, { signal, headers: { Accept: 'application/json' } })
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err
    throw new ApiError('Network error — could not reach Kick', 0)
  }
  if (!res.ok) {
    throw new ApiError(
      res.status === 404 ? 'No such Kick channel' : `Kick returned ${res.status}`,
      res.status,
    )
  }
  return (await res.json()) as T
}

/** Try kick.com first, then vyneer.me's mirror. */
async function getJsonWithFallback<T>(path: string, signal?: AbortSignal): Promise<T> {
  try {
    return await getJson<T>(`${KICK_API}${path}`, signal)
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err
    if (err instanceof ApiError && err.status === 404) throw err
    return await getJson<T>(`${KICK_API_MIRROR}${path}`, signal)
  }
}

/* ------------------------------------------------------------------ *
 * Raw API shapes (only the fields we actually consume)
 * ------------------------------------------------------------------ */

type RawUser = { username: string; profile_pic: string | null } | null
type RawLivestream = { is_live: boolean; session_title: string } | null

type RawChannel = {
  id: number
  slug: string
  user_id: number
  followers_count: number
  is_banned: boolean
  is_affiliate: boolean
  verified: boolean
  vod_enabled: boolean
  user: RawUser
  livestream: RawLivestream
}

type RawVod = {
  id: number
  channel_id: number
  session_title: string
  is_live?: boolean
  created_at: string
  start_time: string
  duration: number
  source: string
  views: number
  thumbnail: { src: string; srcset: string } | null
  video?: { status?: string; is_private?: boolean } | null
}

/* ------------------------------------------------------------------ *
 * Normalizers
 * ------------------------------------------------------------------ */

/**
 * Kick returns `2026-10-12 21:53:43` — a wall-clock timestamp with no timezone.
 * It is UTC; the trailing `Z` is what the reference implementation (orvods-go)
 * assumes as well.
 */
export function parseKickTime(raw: string): number {
  const iso = `${raw.trim().replace(' ', 'T')}Z`
  const ms = Date.parse(iso)
  return Number.isNaN(ms) ? 0 : ms
}

/**
 * Pick the closest srcset candidate at or below `targetWidth`.
 *
 * Kick's descriptors run ~1.33x the pixel count in the filename
 * (`480.webp 640w`, `360.webp 480w`), so 640 targets the 480px file — the
 * right choice for a ~300px-wide grid card on a HiDPI screen.
 */
export function pickThumbnail(thumb: RawVod['thumbnail'], targetWidth: number): string | null {
  if (!thumb) return null
  const candidates = thumb.srcset
    .split(',')
    .map((part) => {
      const [url, width] = part.trim().split(/\s+/)
      return { url, width: Number.parseInt(width ?? '0', 10) || 0 }
    })
    .filter((c) => c.url)
    .sort((a, b) => a.width - b.width)
  if (candidates.length === 0) return thumb.src

  const atOrBelow = candidates.filter((c) => c.width <= targetWidth)
  // Nothing small enough: take the smallest rather than the largest, so an
  // undersized request still lands on the least-wasteful candidate.
  return (atOrBelow.length > 0 ? atOrBelow[atOrBelow.length - 1] : candidates[0]).url
}

function normalizeChannel(raw: RawChannel): Channel {
  const live = raw.livestream ?? { is_live: false, session_title: '' }
  return {
    id: raw.id,
    slug: raw.slug,
    userId: raw.user_id,
    username: raw.user?.username || raw.slug,
    profilePic: raw.user?.profile_pic ?? null,
    followers: raw.followers_count ?? 0,
    isBanned: Boolean(raw.is_banned),
    isAffiliate: Boolean(raw.is_affiliate),
    verified: Boolean(raw.verified),
    vodEnabled: raw.vod_enabled !== false,
    isLive: Boolean(live.is_live),
    liveTitle: live.is_live ? live.session_title : null,
  }
}

function normalizeVod(raw: RawVod, slug: string): Vod {
  return {
    id: raw.id,
    channelSlug: slug,
    title: raw.session_title || 'Untitled stream',
    isLive: raw.is_live === true,
    thumbnail: pickThumbnail(raw.thumbnail, 640),
    views: raw.views ?? 0,
    startTime: parseKickTime(raw.start_time || raw.created_at),
    durationMs: raw.duration ?? 0,
    hlsUrl: raw.source ?? '',
    subscriberOnly: raw.video?.status === 'subscriber_only',
  }
}

/* ------------------------------------------------------------------ *
 * Public API
 * ------------------------------------------------------------------ */

export async function fetchChannel(slug: string, signal?: AbortSignal): Promise<Channel> {
  const raw = await getJsonWithFallback<RawChannel>(`/channels/${encodeURIComponent(slug)}`, signal)
  return normalizeChannel(raw)
}

/**
 * All VODs Kick exposes for a channel: ~30 days, ~20-27 items, newest first.
 * Subscriber-only recordings are included even though Kick omits their source.
 */
export async function fetchVods(slug: string, signal?: AbortSignal): Promise<Vod[]> {
  const raw = await getJsonWithFallback<RawVod[]>(`/channels/${encodeURIComponent(slug)}/videos`, signal)
  if (!Array.isArray(raw)) return []
  return raw
    .map((v) => normalizeVod(v, slug))
    .filter((v) => v.hlsUrl !== '' || v.subscriberOnly)
    .sort((a, b) => b.startTime - a.startTime)
}

/**
 * Kick has no single-VOD endpoint, so resolving a deep link means listing the
 * channel and finding the id.
 */
export async function findVod(slug: string, vodId: number): Promise<Vod | null> {
  const vods = await fetchVods(slug)
  return vods.find((v) => v.id === vodId) ?? null
}
